import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { db } from "@/config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { Card, CardContent } from "@/components/ui/card";
import { calcOCEFromGame, calcKCE } from "@/utils/scoring";
import TrainingStats from "@/components/TrainingStats";

// 🆕 Funktion zum Korrigieren von falsch kodierten Umlauten
const fixUmlauts = (text) => {
  if (!text) return text;
  return text
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ãœ/g, 'Ü')
    .replace(/Ã–/g, 'Ö')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Aae/g, 'ße')
    .replace(/ae/gi, match => match === 'ae' ? 'ä' : 'Ä')
    .replace(/oe/gi, match => match === 'oe' ? 'ö' : 'Ö')
    .replace(/ue/gi, match => match === 'ue' ? 'ü' : 'Ü');
};

export default function Stats() {
  const { user } = useAuth();
  const [tab, setTab] = useState("my");
  const [leaderTab, setLeaderTab] = useState("oce");
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState([]);
  const [kioskSessions, setKioskSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [mvpVotes, setMvpVotes] = useState([]);
  const [tournamentStats, setTournamentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGame, setExpandedGame] = useState(null);
  const [seasonId, setSeasonId] = useState("2026-27"); // Default to current season

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

  useEffect(() => {
    if (!user) return;
    
    (async () => {
      try {
        setLoading(true);
        
        // Load games, stats and users via Backend API (bypasses Firestore rules)
        // Pass season_id filter for games
        let gamesRes, statsRes, usersRes;
        try {
          [gamesRes, statsRes, usersRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/stats/games?season_id=${seasonId}`).then(r => r.json()),
            fetch(`${BACKEND_URL}/api/stats/game_stats`).then(r => r.json()),
            fetch(`${BACKEND_URL}/api/stats/users`).then(r => r.json())
          ]);
        } catch (apiError) {
          console.error("[Stats] API fetch failed:", apiError);
          gamesRes = { ok: false, games: [] };
          statsRes = { ok: false, stats: [] };
          usersRes = { ok: false, users: [] };
        }
        
        // Set games from backend
        const loadedGames = gamesRes.ok ? gamesRes.games : [];
        console.log(`[Stats] Loaded ${loadedGames.length} games via API`);
        setGames(loadedGames);
        
        // Set stats from backend
        const allStats = statsRes.ok ? statsRes.stats : [];
        console.log(`[Stats] Loaded ${allStats.length} total stats via API`);
        
        // Deduplicate stats - take latest per player/game
        const latestStats = {};
        allStats.forEach(stat => {
          const key = `${stat.game_id || stat.event_id}_${stat.player_id}`;
          if (!latestStats[key] || (stat.created_at && latestStats[key].created_at && stat.created_at > latestStats[key].created_at)) {
            latestStats[key] = stat;
          }
        });
        const deduplicatedStats = Object.values(latestStats);
        console.log(`[Stats] Deduplicated to ${deduplicatedStats.length} stats`);
        setStats(deduplicatedStats);
        
        // Set users from backend
        const loadedUsers = usersRes.ok ? usersRes.users : [];
        console.log(`[Stats] Loaded ${loadedUsers.length} users via API`);
        setUsers(loadedUsers);
        
        // Non-critical data - if these fail, just log and continue
        try {
          const kSnap = await getDocs(collection(db, "kiosk_sessions"));
          setKioskSessions(kSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
          console.log("[Stats] Could not load kiosk_sessions:", e.message);
          setKioskSessions([]);
        }
        
        try {
          const mvpSnap = await getDocs(collection(db, "shower_gel_mvp"));
          setMvpVotes(mvpSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
          console.log("[Stats] Could not load shower_gel_mvp:", e.message);
          setMvpVotes([]);
        }

        // Load tournament stats
        if (user.uid) {
          try {
            const tournamentResponse = await fetch(`${BACKEND_URL}/api/tournaments/user/${user.uid}/stats`);
            const tournamentData = await tournamentResponse.json();
            if (tournamentData.ok) {
              setTournamentStats(tournamentData.stats);
            }
          } catch (error) {
            console.log('[Stats] Could not load tournament stats:', error.message);
          }
        }
        
      } catch (error) {
        console.error("Error loading stats data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, BACKEND_URL, seasonId]);  // Add seasonId as dependency

  // Helper: Calculate TeamMargin for a game_stat
  const getTeamMargin = (gameStat) => {
    const event = games.find(g => g.id === gameStat.game_id);
    if (!event || !event.final_home_score || !event.final_away_score) {
      return null; // Kein finales Ergebnis verfügbar
    }
    
    const homeScore = Number(event.final_home_score || 0);
    const awayScore = Number(event.final_away_score || 0);
    
    // TeamMargin = Differenz aus Sicht des eigenen Teams
    // Problem: Wir wissen nicht ob Spieler Home oder Away ist
    // Lösung: Wir nutzen Plus/Minus als Hinweis
    // Wenn Plus/Minus >= 0, gehen wir davon aus dass das Team gewonnen hat
    // Dann ist TeamMargin = Score des Gewinners - Score des Verlierers
    
    const scoreDiff = homeScore - awayScore;
    
    // Wenn Plus/Minus und Score-Diff das gleiche Vorzeichen haben, 
    // ist Spieler wahrscheinlich im Home Team
    if ((gameStat.plus_minus >= 0 && scoreDiff >= 0) || 
        (gameStat.plus_minus < 0 && scoreDiff < 0)) {
      // Spieler ist im Home Team
      return scoreDiff; // Positiv wenn gewonnen, negativ wenn verloren
    } else {
      // Spieler ist im Away Team  
      return -scoreDiff; // Invertiere Vorzeichen
    }
  };

  // Meine Werte
  const myStats = useMemo(() => {
    // Get valid game IDs from existing events
    const validEventIds = games.map(g => g.id);
    
    // Filter stats to only include those from existing events
    const myGameStats = stats.filter(s => 
      s.player_id === user?.uid && validEventIds.includes(s.game_id)
    );
    
    const last5 = myGameStats.slice(-5);
    const lastGame = myGameStats[myGameStats.length - 1];
    
    const oce_last = lastGame ? calcOCEFromGame(lastGame, getTeamMargin(lastGame)).OCE : 0;
    const oce_5 = last5.length > 0 ? last5.reduce((sum, g) => sum + calcOCEFromGame(g, getTeamMargin(g)).OCE, 0) / last5.length : 0;
    const oce_all = myGameStats.length > 0 ? myGameStats.reduce((sum, g) => sum + calcOCEFromGame(g, getTeamMargin(g)).OCE, 0) / myGameStats.length : 0;
    
    // KCE aus Sessions berechnen - mit richtigen Minuten
    const mySessions = kioskSessions.filter(s => s.user_ids?.includes(user?.uid) && s.end_time);
    
    let totalMinutes = 0;
    mySessions.forEach(s => {
      if (s.active_since && s.end_time) {
        const start = s.active_since.toDate?.() || new Date();
        const end = s.end_time.toDate?.() || new Date();
        const sessionMinutes = Math.floor((end - start) / 60000);
        totalMinutes += sessionMinutes;
      }
    });
    
    const totalBeers = mySessions.reduce((sum, s) => sum + (s.beers_by_user?.[user?.uid] || 0), 0);
    const myKCE = calcKCE({ minutes: totalMinutes, beers: totalBeers }).KCE;
    
    const mvpVotes = mySessions.reduce((sum, s) => {
      return sum + (s.mvp_votes?.[user?.uid] || 0);
    }, 0);
    
    return { oce_last, oce_5, oce_all, myKCE, total: oce_last + myKCE, totalMinutes, totalBeers, mvpVotes, sessionCount: mySessions.length, gamesPlayed: myGameStats.length };
  }, [stats, kioskSessions, user, games]);

  // Boxscore On Court
  const courtBoxscore = useMemo(() => {
    const rows = [];
    
    // Get valid game IDs from existing events
    const validEventIds = games.map(g => g.id);
    
    users.forEach(u => {
      const uid = u.auth_uid || u.id;
      
      // Filter stats to only include those from existing events
      const userStats = stats.filter(s => 
        s.player_id === uid && validEventIds.includes(s.game_id)
      );
      
      if (userStats.length === 0) return;
      
      const gamesPlayed = userStats.length;
      const totalPoints = userStats.reduce((sum, s) => sum + (s.points || 0), 0);
      const totalTwos = userStats.reduce((sum, s) => sum + (s.twos || 0), 0);
      const totalThrees = userStats.reduce((sum, s) => sum + (s.threes || 0), 0);
      const totalFTM = userStats.reduce((sum, s) => sum + (s.free_throws_made || 0), 0);
      const totalFTA = userStats.reduce((sum, s) => sum + (s.free_throws_att || 0), 0);
      const totalFouls = userStats.reduce((sum, s) => sum + (s.fouls || 0), 0);
      const totalPlusMinus = userStats.reduce((sum, s) => sum + (s.plus_minus || 0), 0);
      
      const avgPoints = totalPoints / gamesPlayed;
      const avgOCE = userStats.reduce((sum, s) => sum + calcOCEFromGame(s, getTeamMargin(s)).OCE, 0) / gamesPlayed;
      
      rows.push({
        uid,
        name: u.display_name || u.name || u.email,
        games: gamesPlayed,
        totalPoints,
        avgPoints: avgPoints.toFixed(1),
        totalTwos,
        totalThrees,
        totalFTM,
        totalFTA,
        ftPercent: totalFTA > 0 ? ((totalFTM / totalFTA) * 100).toFixed(0) : '0',
        totalFouls,
        avgFouls: (totalFouls / gamesPlayed).toFixed(1),
        totalPlusMinus,
        avgPlusMinus: (totalPlusMinus / gamesPlayed).toFixed(1),
        avgOCE: avgOCE.toFixed(2)
      });
    });
    return rows.sort((a, b) => b.avgOCE - a.avgOCE);
  }, [stats, users]);

  // Boxscore Kiosk
  const kioskBoxscore = useMemo(() => {
    const rows = [];
    users.forEach(u => {
      const uid = u.auth_uid || u.id;
      const userSessions = kioskSessions.filter(s => s.user_ids?.includes(uid) && s.end_time);
      if (userSessions.length === 0) return;
      
      // Berechne Minuten basierend auf active_since -> end_time
      let totalMinutes = 0;
      userSessions.forEach(s => {
        if (s.active_since && s.end_time) {
          const start = s.active_since.toDate?.() || new Date();
          const end = s.end_time.toDate?.() || new Date();
          const sessionMinutes = Math.floor((end - start) / 60000); // ms to minutes
          totalMinutes += sessionMinutes;
        }
      });
      
      const totalBeers = userSessions.reduce((sum, s) => sum + (s.beers_by_user?.[uid] || 0), 0);
      const avgMinutes = totalMinutes / userSessions.length;
      const avgBeers = totalBeers / userSessions.length;
      const beerPerHour = totalMinutes > 0 ? (totalBeers / (totalMinutes / 60)).toFixed(1) : '0.0';
      
      // MVP votes received
      const mvpVotes = userSessions.reduce((sum, s) => {
        return sum + (s.mvp_votes?.[uid] || 0);
      }, 0);
      
      const kce = calcKCE({ minutes: totalMinutes, beers: totalBeers }).KCE;
      
      rows.push({
        uid,
        name: u.display_name || u.name || u.email,
        sessions: userSessions.length,
        totalMinutes,
        totalBeers,
        avgMinutes: avgMinutes.toFixed(1),
        avgBeers: avgBeers.toFixed(1),
        beerPerHour,
        mvpVotes,
        kce: kce.toFixed(2)
      });
    });
    return rows.sort((a, b) => b.kce - a.kce);
  }, [kioskSessions, users]);

  // Leaderboards
  const leaderboard = useMemo(() => {
    const rows = [];
    
    // Get valid game IDs from existing events
    const validEventIds = games.map(g => g.id);
    
    users.forEach(u => {
      const uid = u.auth_uid || u.id;
      
      // Filter stats to only include those from existing events
      const userStats = stats.filter(s => 
        s.player_id === uid && validEventIds.includes(s.game_id)
      );
      
      // Only include users who have played at least one game
      if (userStats.length === 0) return;
      
      const last5 = userStats.slice(-5);
      const lastGame = userStats[userStats.length - 1];
      
      const oce_last = lastGame ? calcOCEFromGame(lastGame, getTeamMargin(lastGame)).OCE : 0;
      const oce_5 = last5.length > 0 ? last5.reduce((sum, g) => sum + calcOCEFromGame(g, getTeamMargin(g)).OCE, 0) / last5.length : 0;
      const oce_all = userStats.length > 0 ? userStats.reduce((sum, g) => sum + calcOCEFromGame(g, getTeamMargin(g)).OCE, 0) / userStats.length : 0;
      
      // KCE - mit richtigen Minuten
      const userSessions = kioskSessions.filter(s => s.user_ids?.includes(uid) && s.end_time);
      let totalMinutes = 0;
      userSessions.forEach(s => {
        if (s.active_since && s.end_time) {
          const start = s.active_since.toDate?.() || new Date();
          const end = s.end_time.toDate?.() || new Date();
          const sessionMinutes = Math.floor((end - start) / 60000);
          totalMinutes += sessionMinutes;
        }
      });
      const totalBeers = userSessions.reduce((sum, s) => sum + (s.beers_by_user?.[uid] || 0), 0);
      const kce = calcKCE({ minutes: totalMinutes, beers: totalBeers }).KCE;
      
      rows.push({
        uid,
        name: u.display_name || u.name || u.email,
        oce_last,
        oce_5,
        oce_all,
        kce,
        total: oce_last + kce
      });
    });
    
    console.log('Leaderboard rows:', rows.length, 'Total stats:', stats.length, 'Total users:', users.length);
    return rows;
  }, [stats, kioskSessions, users]);

  const sortedLeaderboard = useMemo(() => {
    if (leaderTab === 'oce') return [...leaderboard].sort((a, b) => b.oce_all - a.oce_all);
    if (leaderTab === 'kce') return [...leaderboard].sort((a, b) => b.kce - a.kce);
    return [...leaderboard].sort((a, b) => b.total - a.total);
  }, [leaderboard, leaderTab]);

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-4">Statistiken</h2>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-rose-200/70">Lade Statistiken...</p>
        </div>
      ) : (
        <>
          {/* Season Selector */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-rose-200/70">Saison:</span>
            <select
              value={seasonId}
              onChange={(e) => setSeasonId(e.target.value)}
              className="bg-[#1a0d11] border border-white/20 rounded px-2 py-1 text-xs text-white"
              data-testid="season-selector"
            >
              <option value="2026-27">2026-27 (Aktuell)</option>
              <option value="2025-26">2025-26 (Archiv)</option>
            </select>
          </div>

          {/* Main Tabs */}
          <div className="flex gap-1 mb-4 overflow-x-auto">
            <button onClick={() => setTab('my')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'my' ? 'bg-[#b40023]' : 'bg-white/10'}`}>Meine Werte</button>
            <button onClick={() => setTab('games')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'games' ? 'bg-[#b40023]' : 'bg-white/10'}`}>📋 Spiele</button>
            <button onClick={() => setTab('training')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'training' ? 'bg-[#b40023]' : 'bg-white/10'}`}>🏀 Training</button>
            <button onClick={() => setTab('court')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'court' ? 'bg-[#b40023]' : 'bg-white/10'}`}>Boxscore Court</button>
            <button onClick={() => setTab('kiosk')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'kiosk' ? 'bg-[#b40023]' : 'bg-white/10'}`}>Boxscore Kiosk</button>
            <button onClick={() => setTab('leader')} className={`px-3 py-1 text-xs whitespace-nowrap ${tab === 'leader' ? 'bg-[#b40023]' : 'bg-white/10'}`}>Leaderboards</button>
            <button onClick={() => setTab('mvp')} className={`px-3 py-1 text-xs whitespace-nowrap flex items-center gap-1 ${tab === 'mvp' ? 'bg-[#b40023]' : 'bg-white/10'}`}>🚿 Dusch-Gel MVP</button>
            <button onClick={() => setTab('tournaments')} className={`px-3 py-1 text-xs whitespace-nowrap flex items-center gap-1 ${tab === 'tournaments' ? 'bg-[#b40023]' : 'bg-white/10'}`}>🏆 Turniere</button>
            <button onClick={() => setTab('warmer-regen')} className={`px-3 py-1 text-xs whitespace-nowrap flex items-center gap-1 ${tab === 'warmer-regen' ? 'bg-[#b40023]' : 'bg-white/10'}`}>☔ Warmer Regen</button>
          </div>

      {/* Tab: Training Stats */}
      {tab === 'training' && (
        <TrainingStats allUsers={users} />
      )}

      {/* Tab: Meine Werte */}
      {tab === 'my' && (
        <div className="grid gap-3">
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold mb-2">🏀 On Court</p>
              <div className="text-sm space-y-1">
                <div>OCE letztes Spiel: {myStats.oce_last.toFixed(2)}</div>
                <div>OCE letzte 5: {myStats.oce_5.toFixed(2)}</div>
                <div>OCE Gesamt: {myStats.oce_all.toFixed(2)}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold mb-2">🍺 Kiosk</p>
              <div className="text-sm space-y-1">
                <div>KCE Gesamt: {myStats.myKCE.toFixed(2)}</div>
                <div>Sessions: {myStats.sessionCount}</div>
                <div>Minuten: {myStats.totalMinutes}</div>
                <div>Biere: {myStats.totalBeers}</div>
                <div>MVP Votes: {myStats.mvpVotes}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-4">
              <p className="text-sm font-semibold mb-2">⭐ Gesamt</p>
              <div className="text-lg font-bold">{myStats.total.toFixed(2)}</div>
              <div className="text-xs text-rose-200/70">OCE (letztes) + KCE (gesamt)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Spiele - Liste aller Spiele mit aufklappbaren Stats */}
      {tab === 'games' && (
        <div className="space-y-3">
          {/* Aggregierter Gesamt-Boxscore */}
          {(() => {
            // Spiele mit Ergebnis
            const gamesWithResults = games.filter(g => 
              g.final_home_score != null && g.final_away_score != null
            );
            
            if (gamesWithResults.length === 0) return null;
            
            // Berechne Gesamt-Statistiken
            const totalGames = gamesWithResults.length;
            const wins = gamesWithResults.filter(g => {
              return g.team_home 
                ? g.final_home_score > g.final_away_score
                : g.final_away_score > g.final_home_score;
            }).length;
            const losses = totalGames - wins;
            const totalPointsFor = gamesWithResults.reduce((sum, g) => 
              sum + (g.team_home ? (g.final_home_score || 0) : (g.final_away_score || 0)), 0);
            const totalPointsAgainst = gamesWithResults.reduce((sum, g) => 
              sum + (g.team_home ? (g.final_away_score || 0) : (g.final_home_score || 0)), 0);
            
            return (
              <Card className="bg-gradient-to-r from-[#1a0d11] to-[#2a1419] border-yellow-500/30 mb-4">
                <CardContent className="pt-4">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                    📊 Saison-Übersicht
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div className="bg-white/5 rounded-lg p-2">
                      <div className="text-xl font-bold text-rose-200">{totalGames}</div>
                      <div className="text-xs text-rose-200/60">Spiele</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <div className="text-xl font-bold">
                        <span className="text-green-400">{wins}</span>
                        <span className="text-rose-200/40 mx-1">-</span>
                        <span className="text-red-400">{losses}</span>
                      </div>
                      <div className="text-xs text-rose-200/60">W - L</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <div className="text-xl font-bold text-rose-200">
                        {totalPointsFor} : {totalPointsAgainst}
                      </div>
                      <div className="text-xs text-rose-200/60">Punkte</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <div className={`text-xl font-bold ${totalPointsFor - totalPointsAgainst > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {totalPointsFor - totalPointsAgainst > 0 ? '+' : ''}{totalPointsFor - totalPointsAgainst}
                      </div>
                      <div className="text-xs text-rose-200/60">Differenz</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          
          <p className="text-sm font-semibold mb-2">📋 Spiele chronologisch</p>
          {(() => {
            // Nur Spiele mit Ergebnis anzeigen (Backend liefert nur games mit type='game')
            const filteredGames = games.filter(g => {
              const hasResult = g.final_home_score != null || g.final_away_score != null;
              return hasResult;
            });
            
            // Sortiere nach Datum (neueste zuerst)
            return filteredGames.sort((a, b) => {
              const dateA = new Date(a.start_datetime || 0);
              const dateB = new Date(b.start_datetime || 0);
              return dateB - dateA;
            });
          })()
            .map(game => {
              const gameStats = stats.filter(s => s.game_id === game.id || s.event_id === game.id);
              const gameDate = game.start_datetime 
                ? new Date(game.start_datetime).toLocaleDateString('de-DE')
                : 'Datum unbekannt';
              const isExpanded = expandedGame === game.id;
              const result = game.team_home 
                ? `${game.final_home_score}:${game.final_away_score}`
                : `${game.final_away_score}:${game.final_home_score}`;
              const won = game.team_home 
                ? game.final_home_score > game.final_away_score
                : game.final_away_score > game.final_home_score;
              
              return (
                <Card key={game.id} className={`bg-[#1a0d11] border-white/10 ${won ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}`}>
                  <CardContent className="pt-3">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-600/30">
                            {game.team_home ? '🏠 Heimspiel' : '✈️ Auswärtsspiel'}
                          </span>
                          {won ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-green-600/30">✅ Sieg</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-600/30">❌ Niederlage</span>
                          )}
                        </div>
                        <p className="text-sm font-semibold">
                          vs. {fixUmlauts(game.opponent) || 'Unbekannt'}
                        </p>
                        <p className="text-xs text-rose-200/70">
                          {gameDate} • {result} • {gameStats.length} Spieler
                        </p>
                      </div>
                      <button className="text-2xl">
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                    
                    {/* Aufgeklappte Statistiken */}
                    {isExpanded && gameStats.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs font-semibold mb-3">📊 Spieler-Statistiken:</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-white/10 bg-white/5">
                                <th className="text-left p-2">Spieler</th>
                                <th className="text-center p-2">Min</th>
                                <th className="text-center p-2">Pts</th>
                                <th className="text-center p-2">2P</th>
                                <th className="text-center p-2">3P</th>
                                <th className="text-center p-2">FW</th>
                                <th className="text-center p-2">PF</th>
                                <th className="text-center p-2">+/−</th>
                                <th className="text-center p-2">OCE</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Berechne teamMargin für OCE
                                const teamMargin = game.team_home 
                                  ? (game.final_home_score || 0) - (game.final_away_score || 0)
                                  : (game.final_away_score || 0) - (game.final_home_score || 0);
                                
                                console.log(`Game: ${game.opponent}, teamMargin: ${teamMargin}, team_home: ${game.team_home}`);
                                console.log(`Game scores: home=${game.final_home_score}, away=${game.final_away_score}`);
                                
                                return gameStats
                                  .sort((a, b) => {
                                    const oceA = calcOCEFromGame(a, teamMargin).OCE || 0;
                                    const oceB = calcOCEFromGame(b, teamMargin).OCE || 0;
                                    return oceB - oceA; // Höchste OCE zuerst
                                  })
                                  .map(stat => {
                                    const playerUser = users.find(u => u.id === stat.player_id || u.auth_uid === stat.player_id);
                                    const playerName = playerUser?.display_name || playerUser?.name || playerUser?.displayName || playerUser?.email?.split('@')[0] || 'Unbekannt';
                                    const oce = calcOCEFromGame(stat, teamMargin).OCE;
                                    console.log(`Player: ${playerName}, minutes: ${stat.minutes}, points: ${stat.points}, +/-: ${stat.plus_minus}, OCE: ${oce}`);
                                  
                                  return (
                                    <tr key={stat.id} className={`border-b border-white/10 ${stat.player_id === user?.uid ? 'bg-blue-900/20' : ''}`}>
                                      <td className="p-2 font-medium">
                                        {playerName}
                                        {stat.player_id === user?.uid && ' (Du)'}
                                      </td>
                                      <td className="text-center p-2">{Math.round(stat.minutes || 0)}</td>
                                      <td className="text-center p-2 font-semibold">{stat.points || 0}</td>
                                      <td className="text-center p-2">{stat.twos || 0}</td>
                                      <td className="text-center p-2">{stat.threes || 0}</td>
                                      <td className="text-center p-2">{stat.free_throws_made || 0}/{stat.free_throws_att || 0}</td>
                                      <td className="text-center p-2">{stat.fouls || 0}</td>
                                      <td className={`text-center p-2 ${(stat.plus_minus || 0) > 0 ? 'text-green-400' : (stat.plus_minus || 0) < 0 ? 'text-red-400' : ''}`}>
                                        {(stat.plus_minus || 0) > 0 ? '+' : ''}{stat.plus_minus || 0}
                                      </td>
                                      <td className="text-center p-2 font-semibold text-yellow-400">
                                        {(oce !== null && oce !== undefined && typeof oce === 'number') ? oce.toFixed(2) : '-'}
                                      </td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Team OCE Zusammenfassung */}
                        <div className="mt-3 p-3 bg-[#2a1419] rounded border border-yellow-500/30">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold">Team OCE Durchschnitt:</span>
                            <span className="text-lg font-bold text-yellow-400">
                              {(() => {
                                const teamMargin = game.team_home 
                                  ? (game.final_home_score || 0) - (game.final_away_score || 0)
                                  : (game.final_away_score || 0) - (game.final_home_score || 0);
                                const validOCEs = gameStats
                                  .map(s => calcOCEFromGame(s, teamMargin).OCE)
                                  .filter(oce => oce !== null && oce !== undefined && typeof oce === 'number');
                                const avgOCE = validOCEs.length > 0 
                                  ? (validOCEs.reduce((a, b) => a + b, 0) / validOCEs.length).toFixed(2)
                                  : '-';
                                return avgOCE;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {isExpanded && gameStats.length === 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-rose-200/70 text-center">Keine Statistiken für dieses Spiel vorhanden</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          
          {games.filter(g => g.final_home_score != null || g.final_away_score != null).length === 0 && (
            <Card className="bg-[#1a0d11] border-white/10">
              <CardContent className="pt-4">
                <p className="text-rose-200/80 text-xs text-center">Noch keine Spiele mit Ergebnissen vorhanden</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}


      {/* Tab: Boxscore Court */}
      {tab === 'court' && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-2">Spieler</th>
                <th className="text-center p-2">Spiele</th>
                <th className="text-center p-2">Pkt Total</th>
                <th className="text-center p-2">Ø Pkt</th>
                <th className="text-center p-2">2er</th>
                <th className="text-center p-2">3er</th>
                <th className="text-center p-2">FT</th>
                <th className="text-center p-2">FT%</th>
                <th className="text-center p-2">Fouls</th>
                <th className="text-center p-2">+/-</th>
                <th className="text-center p-2">Ø OCE</th>
              </tr>
            </thead>
            <tbody>
              {courtBoxscore.map(r => (
                <tr key={r.uid} className="border-b border-white/10">
                  <td className="p-2">{r.name}</td>
                  <td className="text-center p-2">{r.games}</td>
                  <td className="text-center p-2 font-semibold">{r.totalPoints}</td>
                  <td className="text-center p-2">{r.avgPoints}</td>
                  <td className="text-center p-2">{r.totalTwos}</td>
                  <td className="text-center p-2">{r.totalThrees}</td>
                  <td className="text-center p-2">{r.totalFTM}/{r.totalFTA}</td>
                  <td className="text-center p-2">{r.ftPercent}%</td>
                  <td className="text-center p-2">{r.totalFouls}</td>
                  <td className="text-center p-2">{r.totalPlusMinus > 0 ? '+' : ''}{r.totalPlusMinus}</td>
                  <td className="text-center p-2 font-semibold text-yellow-400">{r.avgOCE}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Boxscore Kiosk */}
      {tab === 'kiosk' && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-2">Spieler</th>
                <th className="text-center p-2">Sessions</th>
                <th className="text-center p-2">Min Total</th>
                <th className="text-center p-2">🍺 Total</th>
                <th className="text-center p-2">Ø Min</th>
                <th className="text-center p-2">Ø 🍺</th>
                <th className="text-center p-2">🍺/h</th>
                <th className="text-center p-2">MVP</th>
                <th className="text-center p-2">KCE</th>
              </tr>
            </thead>
            <tbody>
              {kioskBoxscore.map(r => (
                <tr key={r.uid} className="border-b border-white/10">
                  <td className="p-2">{r.name}</td>
                  <td className="text-center p-2">{r.sessions}</td>
                  <td className="text-center p-2">{r.totalMinutes}</td>
                  <td className="text-center p-2 text-yellow-400">{r.totalBeers}</td>
                  <td className="text-center p-2">{r.avgMinutes}</td>
                  <td className="text-center p-2">{r.avgBeers}</td>
                  <td className="text-center p-2">{r.beerPerHour}</td>
                  <td className="text-center p-2">{r.mvpVotes > 0 ? `🏆 ${r.mvpVotes}` : '-'}</td>
                  <td className="text-center p-2 font-semibold">{r.kce}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Leaderboards */}
      {tab === 'leader' && (
        <div>
          <div className="flex gap-1 mb-4">
            <button onClick={() => setLeaderTab('oce')} className={`px-3 py-1 text-xs ${leaderTab === 'oce' ? 'bg-[#b40023]' : 'bg-white/10'}`}>OCE</button>
            <button onClick={() => setLeaderTab('kce')} className={`px-3 py-1 text-xs ${leaderTab === 'kce' ? 'bg-[#b40023]' : 'bg-white/10'}`}>KCE</button>
            <button onClick={() => setLeaderTab('total')} className={`px-3 py-1 text-xs ${leaderTab === 'total' ? 'bg-[#b40023]' : 'bg-white/10'}`}>Gesamt</button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Spieler</th>
                  {leaderTab === 'oce' && <th className="text-left p-2">OCE Ø</th>}
                  {leaderTab === 'kce' && <th className="text-left p-2">KCE</th>}
                  {leaderTab === 'total' && (
                    <>
                      <th className="text-left p-2">OCE letztes</th>
                      <th className="text-left p-2">KCE</th>
                      <th className="text-left p-2">Total</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((r, i) => (
                  <tr key={r.uid} className={`border-b border-white/10 ${r.uid === user?.uid ? 'bg-[#b40023]/20' : ''}`}>
                    <td className="p-2">{i + 1}</td>
                    <td className="p-2">{r.name}</td>
                    {leaderTab === 'oce' && <td className="p-2 font-semibold">{r.oce_all.toFixed(2)}</td>}
                    {leaderTab === 'kce' && <td className="p-2 font-semibold">{r.kce.toFixed(2)}</td>}
                    {leaderTab === 'total' && (
                      <>
                        <td className="p-2">{r.oce_last.toFixed(2)}</td>
                        <td className="p-2">{r.kce.toFixed(2)}</td>
                        <td className="p-2 font-semibold">{r.total.toFixed(2)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Tab: Dusch-Gel MVP Leaderboard */}
      {tab === 'mvp' && (
        <Card className="bg-[#1a0d11] border-white/10">
          <CardContent className="pt-4">
            <h3 className="font-bold mb-3 text-lg flex items-center gap-2">
              🚿 Dusch-Gel MVP Award - Ewige Bestenliste
            </h3>
            {(() => {
              // Count votes per player
              const voteCounts = mvpVotes.reduce((acc, vote) => {
                acc[vote.player_id] = (acc[vote.player_id] || 0) + 1;
                return acc;
              }, {});
              
              // Sort by votes
              const sortedPlayers = Object.entries(voteCounts)
                .sort(([, a], [, b]) => b - a)
                .map(([playerId, count]) => {
                  const user = users.find(u => u.auth_uid === playerId || u.id === playerId);
                  return {
                    playerId,
                    name: user?.display_name || user?.name || 'Unbekannt',
                    votes: count
                  };
                });
              
              if (sortedPlayers.length === 0) {
                return <p className="text-sm text-rose-200/60">Noch keine Votes vorhanden</p>;
              }
              
              return (
                <div className="space-y-2">
                  {sortedPlayers.map((player, index) => (
                    <div
                      key={player.playerId}
                      className={`flex items-center justify-between p-3 rounded border ${
                        index === 0 ? 'bg-yellow-900/20 border-yellow-500/30' :
                        index === 1 ? 'bg-gray-700/20 border-gray-500/30' :
                        index === 2 ? 'bg-orange-900/20 border-orange-500/30' :
                        'bg-[#160b0f] border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold min-w-[3rem]">
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && `${index + 1}.`}
                        </span>
                        <span className="font-semibold">{player.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🚿</span>
                        <span className="font-bold text-lg">{player.votes}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Tab: Turniere */}
      {tab === 'tournaments' && (
        <Card className="bg-[#1a0d11] border-white/10">
          <CardContent className="pt-4">
            <h2 className="text-xl font-semibold text-rose-200 mb-4">🏆 Turnier-Statistiken</h2>

            {!tournamentStats ? (
              <div className="text-center py-8 text-rose-200/50">
                Lade Turnier-Daten...
              </div>
            ) : tournamentStats.tournaments_played === 0 ? (
              <div className="text-center py-8 text-rose-200/50">
                Noch keine Turniere gespielt
              </div>
            ) : (
              <div className="space-y-6">
                {/* Overall Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white/5 p-3 rounded">
                    <div className="text-xs text-rose-200/60">Turniere</div>
                    <div className="text-2xl font-bold text-rose-200">{tournamentStats.tournaments_played}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded">
                    <div className="text-xs text-rose-200/60">Spiele</div>
                    <div className="text-2xl font-bold text-rose-200">{tournamentStats.total_games}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded">
                    <div className="text-xs text-rose-200/60">Siege / Niederlagen</div>
                    <div className="text-2xl font-bold text-green-400">{tournamentStats.total_wins}</div>
                    <div className="text-sm text-red-400">/ {tournamentStats.total_losses}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded">
                    <div className="text-xs text-rose-200/60">Punktedifferenz</div>
                    <div className={`text-2xl font-bold ${
                      tournamentStats.total_diff > 0 ? 'text-green-400' : 
                      tournamentStats.total_diff < 0 ? 'text-red-400' : 'text-rose-200'
                    }`}>
                      {tournamentStats.total_diff > 0 ? '+' : ''}{tournamentStats.total_diff}
                    </div>
                  </div>
                </div>

                {/* Detailed Stats */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white/5 p-3 rounded">
                      <div className="text-rose-200/60 mb-1">Punkte erzielt</div>
                      <div className="text-xl font-bold text-green-400">{tournamentStats.total_points_for}</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded">
                      <div className="text-rose-200/60 mb-1">Punkte kassiert</div>
                      <div className="text-xl font-bold text-red-400">{tournamentStats.total_points_against}</div>
                    </div>
                  </div>
                </div>

                {/* Per Tournament */}
                <div>
                  <h3 className="text-sm font-semibold text-rose-200/70 mb-3">Pro Turnier</h3>
                  <div className="space-y-2">
                    {tournamentStats.tournaments.map((tournament, index) => (
                      <div key={index} className="bg-white/5 p-3 rounded">
                        <div className="font-semibold text-rose-200 mb-2">{tournament.tournament_name}</div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div>
                            <span className="text-rose-200/60">Spiele:</span>
                            <span className="ml-1 font-semibold">{tournament.games}</span>
                          </div>
                          <div>
                            <span className="text-rose-200/60">W/L:</span>
                            <span className="ml-1 font-semibold text-green-400">{tournament.wins}</span>
                            <span className="text-rose-200/60">/</span>
                            <span className="font-semibold text-red-400">{tournament.losses}</span>
                          </div>
                          <div>
                            <span className="text-rose-200/60">Diff:</span>
                            <span className={`ml-1 font-semibold ${
                              tournament.diff > 0 ? 'text-green-400' : 
                              tournament.diff < 0 ? 'text-red-400' : 'text-rose-200'
                            }`}>
                              {tournament.diff > 0 ? '+' : ''}{tournament.diff}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Warmer Regen */}
      {tab === 'warmer-regen' && (
        <div className="space-y-4">
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-4">
              <div className="text-center space-y-3">
                <h2 className="text-3xl font-bold text-rose-200">☔ Warmer Regen</h2>
                <p className="text-base text-rose-200/80">
                  Spread Love 4 xmas 2025 🎄
                </p>
                <p className="text-sm text-rose-200/60 italic">
                  Für jeden soviel Liebe spreaden wie es nur geht!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Spracheingabe Hinweis */}
          <Card className="bg-blue-900/20 border-blue-500/30">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🎤</span>
                <div>
                  <p className="text-sm font-semibold text-blue-200 mb-1">
                    Spracheingabe nutzen!
                  </p>
                  <p className="text-xs text-blue-200/70">
                    Tippe auf ein Textfeld und nutze das <strong>Mikrofon-Symbol</strong> auf deiner Tastatur 
                    (meist unten links oder rechts). Diktiere deine Nachricht und sie wird automatisch eingetippt!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Google Forms Embed */}
          <Card className="bg-[#1a0d11] border-white/10 overflow-hidden">
            <div className="w-full">
              <iframe 
                src="https://docs.google.com/forms/d/e/1FAIpQLSeA2PzPbrw9yDBHQuDnw-1O6alcIPm1uwD6BN-W-fsF6VLQfQ/viewform?embedded=true" 
                width="100%" 
                height="2000" 
                frameBorder="0" 
                marginHeight="0" 
                marginWidth="0"
                className="w-full"
                title="Warmer Regen Form"
              >
                Wird geladen…
              </iframe>
            </div>
          </Card>
        </div>
      )}
        </>
      )}
    </div>
  );
}
